/**
 * Public Affiliate Partner Agreement page.
 *
 * This page displays the v1 draft agreement text for shareability and
 * transparency. The AUTHORITATIVE copy for consent purposes is the copy
 * stored server-side in `affiliate_terms_versions` and rendered inline
 * inside /affiliate/apply (via the getAffiliateApplyContext RPC). If the
 * two ever diverge, the server copy wins and the applicant sees the
 * server copy when accepting.
 */
import { createFileRoute } from "@tanstack/react-router";
import { PolicyLayout } from "@/components/PolicyLayout";

export const AFFILIATE_TERMS_VERSION = "v1";
export const AFFILIATE_TERMS_EFFECTIVE = "13 July 2026";

export const Route = createFileRoute("/policies/affiliate-terms")({
  component: AffiliateTermsPage,
  head: () => ({
    meta: [
      { title: "Affiliate Partner Agreement. TruWe" },
      {
        name: "description",
        content:
          "TruWe Affiliate Partner Agreement (v1 preview draft), commission structure, disclosure duties, payouts, reversals, and applicable Indian law.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/policies/affiliate-terms" }],
  }),
});

function AffiliateTermsPage() {
  return (
    <PolicyLayout title="Affiliate Partner Agreement" updated={AFFILIATE_TERMS_EFFECTIVE}>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">Preview only, pending Indian counsel review.</p>
        <p className="mt-1">
          Version <code className="rounded bg-white/60 px-1">{AFFILIATE_TERMS_VERSION}</code>{" "}
          &middot; effective {AFFILIATE_TERMS_EFFECTIVE}. This document is provided in plain
          English and <strong>is pending independent review by Indian counsel</strong>. The
          TruWe Affiliate Programme is not yet live. No representation of legal approval
          is made or should be inferred.
        </p>
        <p className="mt-2">
          Applicable frameworks referenced in this Agreement include (without limitation):
          the Indian Contract Act, 1872 (ss. 73-74); the Information Technology Act, 2000
          (ss. 4, 7, 10A, 11-13); the Bharatiya Sakshya Adhiniyam, 2023 (ss. 61-63); the
          Consumer Protection Act, 2019 and the Consumer Protection (E-Commerce) Rules,
          2020; the CCPA Misleading Advertisements and Endorsements Guidelines, 2022; the
          Food Safety and Standards Act, 2006 with the FSS Advertising and Claims
          Regulations, 2018 and applicable nutraceutical rules; the Drugs and Magic
          Remedies (Objectionable Advertisements) Act, 1954; the DPDP Act, 2023 and DPDP
          Rules, 2025 (to the extent in force / applicable); and the ASCI Code /
          Influencer Guidelines (which are self-regulatory). Tax / TDS treatment applies
          only per a chartered accountant's advice.
        </p>
      </div>

      <article id="tw-affiliate-agreement-v1" className="prose prose-neutral mt-8 max-w-none">
        <h2>1. Eligibility and authority</h2>
        <p>
          You must be at least 18 years old, resident in India, and legally able to enter into
          contracts and receive payments. If you are applying on behalf of an entity, you
          confirm that you have the authority to bind it. You are solely responsible for tax
          compliance in your jurisdiction.
        </p>

        <h2>2. Application and discretionary approval</h2>
        <p>
          Submission of the application form is an offer to enter into this Agreement on
          TruWe's discretion. TruWe may accept, reject, pause or terminate any application
          without giving reasons. Nothing in this Agreement guarantees approval or continued
          participation.
        </p>

        <h2>3. Unique code, link and attribution</h2>
        <p>
          Upon approval, TruWe issues you a unique affiliate coupon code and share link.
          Attribution of a sale is determined by the code entered at checkout or the
          last-touch link on TruWe's servers; TruWe's records are conclusive.
        </p>

        <h2>4. Commission structure</h2>
        <p>Commission is tier-based on validated matured orders:</p>
        <ul>
          <li><strong>1st sale</strong>: 10% of the eligible pre-GST merchandise value.</li>
          <li><strong>10th sale onwards</strong>: 12%.</li>
          <li><strong>30th sale onwards</strong>: 15%.</li>
        </ul>
        <p>
          The tier counter is applied per calendar month based on delivered and matured
          orders only. Pending, held, refunded, RTO, cancelled, chargeback and fraud-reversed
          orders do not count.
        </p>

        <h2>5. Basis of commission</h2>
        <p>
          Commission is calculated on the pre-GST merchandise value <strong>after</strong>{" "}
          your affiliate discount and <strong>before</strong> GST, shipping, COD or handling
          fees. GST (currently 5%), shipping and any other statutory or platform fees are
          excluded from the commission base.
        </p>

        <h2>6. Prepaid only</h2>
        <p>
          Commission is earned <strong>only on prepaid orders</strong>. Cash-on-Delivery
          (COD) orders earn no commission, regardless of whether they are ultimately
          delivered.
        </p>

        <h2>7. Delivery and maturity</h2>
        <p>
          An order becomes eligible for commission only after (a) successful delivery and
          (b) 3 full days beyond the customer's return window closing. During this window,
          commissions are held in <em>pending</em> status and are not visible to you.
        </p>

        <h2>8. Pending amounts are not visible</h2>
        <p>
          To avoid confusion and prevent leakage of order-level information, your dashboard
          shows only matured payable commission and paid history. Pending, held and rejected
          amounts are structurally hidden.
        </p>

        <h2>9. Reversals</h2>
        <p>
          If an order is returned, refunded (full or partial), exchanged for a lower value,
          marked RTO, cancelled, charged back, or found to be fraudulent, the corresponding
          commission is reversed. Partial refunds reduce the commission proportionally;
          ambiguous partials are held pending review. Reversals may create a negative
          carry-forward against future earnings.
        </p>

        <h2>10. Minimum payout and manual processing</h2>
        <p>
          Payouts are made manually on a schedule set by TruWe. The minimum manual payout
          threshold is <strong>₹1,000</strong>. Amounts below the threshold roll forward.
          TruWe may deduct CA-approved statutory amounts (e.g. TDS) from each payout;
          deductions are recorded in your ledger.
        </p>

        <h2>11. Independent contractor</h2>
        <p>
          You are an independent contractor. This Agreement does not create employment,
          agency, franchise or partnership. You may not represent yourself as a TruWe
          employee, agent or spokesperson.
        </p>

        <h2>12. No guaranteed earnings</h2>
        <p>
          TruWe makes no representations about earnings potential. Actual results depend on
          your audience, content and conversion.
        </p>

        <h2>13. Truthful content and prohibited claims</h2>
        <p>
          You will not make (i) medical, therapeutic, disease, or cure claims; (ii)
          misleading claims about TruWe or its products; or (iii) claims not supported by
          product labelling and TruWe-approved messaging. You will comply with FSSAI
          advertising rules, the Consumer Protection Act 2019, the Consumer Protection
          (E-Commerce) Rules 2020, the Drugs and Magic Remedies (Objectionable
          Advertisements) Act 1954, and all other applicable Indian laws.
        </p>

        <h2>14. Brand and intellectual property</h2>
        <p>
          TruWe grants you a non-exclusive, non-transferable, revocable licence to use
          TruWe's approved brand assets solely to promote TruWe products under this
          Agreement. All goodwill accrues to TruWe. You will not register TruWe's marks or
          confusingly similar marks.
        </p>

        <h2>15. Prohibited practices</h2>
        <p>
          You will not: bid on TruWe trademark terms in paid search; engage in spam or
          unsolicited messaging; place self-orders using your own code; stuff cookies; buy
          fake traffic; use bots; publish your code in coupon-aggregator sites without
          written permission; share your code with resellers; or engage in any deceptive
          conduct.
        </p>

        <h2>16. ASCI material-connection disclosure</h2>
        <p>
          You will disclose your material connection to TruWe on every promotional post, in
          a clear and prominent way, in line with the ASCI Guidelines for Influencer
          Advertising in Digital Media. Acceptable labels include #Ad, #Advert,
          #Advertisement, #Sponsored, #Collab, #Partnership. Disguised advertising,
          disclosures buried in comments, or expandable "read more" hiding are not
          acceptable.
        </p>

        <h2>17. Confidentiality</h2>
        <p>
          You will keep confidential any non-public information TruWe shares (pricing,
          roadmaps, formulations, launch plans).
        </p>

        <h2>18. KYC, PAN and bank details</h2>
        <p>
          To receive payouts, you must supply PAN and bank details post-approval through
          the secure onboarding flow. TruWe stores these values encrypted. TruWe does{" "}
          <strong>not</strong> collect Aadhaar.
        </p>

        <h2>19. Privacy and retention</h2>
        <p>
          Your data is processed as described in the TruWe Privacy Policy, for the specific
          purposes of application review, KYC, payout, tax compliance, fraud prevention, and
          dispute resolution. Retention aligns with statutory and audit needs.
        </p>

        <h2>20. Audit and cooperation</h2>
        <p>
          You will cooperate with TruWe's investigations of suspected fraud, chargebacks, or
          ASCI-related complaints, and will provide reasonable evidence upon request.
        </p>

        <h2>21. Suspension and termination</h2>
        <p>
          TruWe may suspend, pause or terminate your participation at any time with or
          without cause. On termination, unmatured commissions may be forfeited; matured
          commissions above the minimum payout threshold, less any reversals, will be paid.
        </p>

        <h2>22. Amendments and re-consent</h2>
        <p>
          TruWe may update this Agreement. Material updates require your fresh consent
          before continued participation; existing acceptance receipts remain unchanged.
        </p>

        <h2>23. Notices</h2>
        <p>
          Notices to you: to the email and WhatsApp number on file. Notices to TruWe:{" "}
          <a href="mailto:support@truwe.in">support@truwe.in</a>.
        </p>

        <h2>24. Indemnity and limitation of liability</h2>
        <p>
          Subject to applicable law, you will indemnify TruWe against third-party claims
          arising from your breach of this Agreement or your promotional conduct. TruWe's
          aggregate liability under this Agreement will not exceed commissions paid to you
          in the 3 months preceding the claim.
        </p>

        <h2>25. Governing law and venue</h2>
        <p>
          Governed by the laws of India. Courts at Bengaluru have exclusive jurisdiction,
          subject to the consumer-forum rights of applicable law.
        </p>

        <h2>26. Grievance and contact</h2>
        <p>
          Grievance officer: <a href="mailto:grievance@truwe.in">grievance@truwe.in</a>. See
          our Privacy Policy for postal and grievance details.
        </p>

        <h2>27. Survival and severability</h2>
        <p>
          Clauses on IP, confidentiality, indemnity, liability limits, governing law, audit
          and reversals survive termination. If any clause is unenforceable, the rest
          remain in force.
        </p>

        <p data-terms-end>
          <strong>End of Agreement v{AFFILIATE_TERMS_VERSION.replace("v", "")}.</strong> By
          ticking "I accept the Affiliate Partner Agreement" on the application form, you
          confirm you have read and agree to be bound by the version identified above.
        </p>
      </article>
    </PolicyLayout>
  );
}
